interface FrontAPI {
  getBasketInfo(): any;
  setBasketInfo(basket: any): any;
  getLang(): string;
  getCategories(): any[];
}

declare var llCategories: string[];
declare var llPaymentLogoEnabled: boolean;
declare var llLanguageMode: string;

class LeaseLinkPayment {
  static readonly MIN_NET_VALUE = 300;
  static readonly MAX_NET_VALUE = 150000;
  private frontAPI: FrontAPI | null = null;
  private excludedPaymentMessage =
    "Jeden z produktów jest w kategorii wykluczonej z płatności";
  private basketValueMessage =
    "Wartość koszyka poza dostępnym zakresem (300-150000 zł netto)";
  private labelText = "LeaseLink";

  private readonly allowedCategories =
    typeof llCategories !== "undefined" ? llCategories : [];
  
  private readonly paymentLogoEnabled =
    typeof llPaymentLogoEnabled !== "undefined" ? llPaymentLogoEnabled : true;

  constructor() {
    this.init().catch(error => console.error("Payment initialization failed:", error));
  }

  private async init() {
    try {
      this.frontAPI = await this.detectFrontAPI();
      
      // Sprawdź dostępność językową
      if (!this.isLanguageAvailable()) {
        this.disablePayment();
        return;
      }
      
      const basketData = this.getBasketData();
      const hasDisallowed = await this.hasDisallowedProducts(basketData);
      const isOutOfRange = await this.isBasketValueOutOfRange(basketData);
      
      if (hasDisallowed || isOutOfRange) {
        // Priorytet: kategoria > wartość
        const message = hasDisallowed ? this.excludedPaymentMessage : this.basketValueMessage;
        this.disablePayment(message);
      }
      
      if (this.paymentLogoEnabled) {
        this.addLeaseLinkLogo();
      }
    } catch (error) {
      console.error("Failed to detect frontAPI:", error);
    }
  }

  private async detectFrontAPI() {
    return new Promise<FrontAPI>((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 20;

      const interval = setInterval(() => {
        if ((window as any).frontAPI) {
          clearInterval(interval);
          resolve((window as any).frontAPI);
        }

        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          reject(new Error("frontAPI not found after timeout"));
        }
      }, 500);
    });
  }

  private isLanguageAvailable(): boolean {
    if (typeof llLanguageMode === "undefined" || llLanguageMode === "all") {
      return true;
    }

    if (llLanguageMode === "pl_only") {
      if (this.frontAPI && this.frontAPI.getLang) {
        const currentLang = this.frontAPI.getLang();
        return currentLang === "pl_PL";
      }
      return true; // Jeśli nie można pobrać języka, zakładamy dostępność
    }

    return true;
  }

  private getBasketData(): any {
    if (!this.frontAPI) return null;
    try {
      return this.frontAPI.getBasketInfo();
    } catch (error) {
      console.warn("getBasketInfo failed:", error);
      return null;
    }
  }

  private async hasDisallowedProducts(basket: any): Promise<boolean> {
    try {
      if (!basket || !basket.products || this.allowedCategories.length === 0)
        return false;

      const hasDisallowedProducts = basket.products.some(
        (product: any) => !this.isInAllowedCategories(product)
      );

      return hasDisallowedProducts;
    } catch (error) {
      console.error("Failed to check basket products:", error);
      return false;
    }
  }

  private async isBasketValueOutOfRange(basketData: any): Promise<boolean> {
    try {
      if (!basketData || !basketData.basket) return false;

      const sumProductsFloat = basketData.basket.sum_products_float || 0;
      const taxes = basketData.taxes || {};

      // Zsumuj wszystkie wartości VAT z obiektu taxes
      const sumVAT = Object.values(taxes).reduce(
        (sum: number, value: any) => sum + (parseFloat(value) || 0),
        0
      );

      // Oblicz wartość netto
      const netValue = sumProductsFloat - sumVAT;

      // Sprawdź czy wykracza poza zakres
      return netValue < LeaseLinkPayment.MIN_NET_VALUE || netValue > LeaseLinkPayment.MAX_NET_VALUE;
    } catch (error) {
      console.error("Failed to check basket value:", error);
      return false;
    }
  }

  private isInAllowedCategories(product: any) {
    if (this.allowedCategories.length === 0) {
      return true;
    }

    const productCategoryId =
      product.category?.id ?? product.category?.category_id;

    if (productCategoryId == null) return true;

    if (this.allowedCategories.indexOf(productCategoryId.toString()) !== -1) {
      return true;
    }

    const allCategories = this.frontAPI!.getCategories();

    const categoryPath = LeaseLinkPayment.findCategoryPath(
      allCategories,
      productCategoryId
    );

    for (const category of categoryPath) {
      if (this.allowedCategories.indexOf(category.id) !== -1) {
        return true;
      }
    }

    return false;
  }

  static findCategoryPath(
    categories: any[],
    targetId: number | string
  ): { id: string; name: string }[] {
    const normalizedTarget = targetId.toString();
    for (const category of categories) {
      if (category.id.toString() === normalizedTarget) {
        return [{ id: category.id.toString(), name: category.name }];
      }

      if (category.children && category.children.length > 0) {
        const path = LeaseLinkPayment.findCategoryPath(
          category.children,
          targetId
        );
        if (path.length > 0) {
          return [{ id: category.id.toString(), name: category.name }, ...path];
        }
      }
    }
    return [];
  }

  private findLabelWithText(text: string): Element | null {
    const labels = document.querySelectorAll(".payment:not(.none) label");
    return (
      Array.from(labels).find((label) => label.textContent?.includes(text)) ||
      null
    );
  }

  private addLeaseLinkLogo() {
    const leaseLinkLabelElement = this.findLabelWithText(this.labelText);
    if (!leaseLinkLabelElement) return;

    const paymentElement = leaseLinkLabelElement.closest(
      ".payment"
    ) as HTMLInputElement;
    if (!paymentElement) return;
    const style = document.createElement("style");
    style.textContent = `
      .payment-with-logo.lease-link > .name > label{
        position: relative !important;
        color: transparent !important;
        letter-spacing: -8px !important;
      }
      .payment-with-logo.lease-link > .name > label::before {
        content: "";
        display: inline-block;
        vertical-align: text-bottom;
        width: 60px;
        height: 20px; 
        margin-right: 5px;
        background-image: url("data:image/svg+xml;base64,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");
        background-size: contain;
        background-repeat: no-repeat;
        background-position: left center;
      }
    `;
    document.head.appendChild(style);
    paymentElement.classList.add("payment-with-logo", "lease-link");
  }

  private disablePayment(message?: string) {
    const leaseLinkLabelElement = this.findLabelWithText(this.labelText);
    if (!leaseLinkLabelElement) return;

    const paymentElement = leaseLinkLabelElement.closest(
      ".payment"
    ) as HTMLInputElement;
    if (!paymentElement) return;

    paymentElement.classList.remove("selected");

    const radio = paymentElement.querySelector(
      'input[type="radio"]'
    ) as HTMLInputElement;

    if (radio) {
      radio.disabled = true;
      radio.checked = false;

      paymentElement.style.opacity = "0.5";
      paymentElement.title = message || this.excludedPaymentMessage;
      paymentElement.style.cursor = "help";
      paymentElement.style.fontStyle = "italic";
    }

    const labels = paymentElement.querySelectorAll("label");
    labels.forEach((label) => {
      label.style.cursor = "help";
    });

    const descriptionEl = paymentElement.querySelector(
      ".description"
    ) as HTMLInputElement;
    if (descriptionEl) {
      descriptionEl.textContent = " (niedostępne)";
    }

    const payments = document.querySelectorAll('.payment input[type="radio"]');
    const firstAvailable = Array.from(payments).find(
      (radio) => !(radio as HTMLInputElement).disabled
    ) as HTMLInputElement;

    if (firstAvailable) {
      firstAvailable.checked = true;
    }
  }
}

// Initialize the payment handler
new LeaseLinkPayment();
